// Shared canvas/image helpers for the Before & After tools (composer, collage, video).

// Knock out near-white pixels so a logo sits on any background with no white box.
// threshold 180..255 — lower removes light greys too (more aggressive), higher only near-white.
export function knockoutWhite(dataUrl, threshold = 238) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth || 300;
        c.height = img.naturalHeight || 150;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, c.width, c.height);
        const d = data.data;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i] >= threshold && d[i + 1] >= threshold && d[i + 2] >= threshold) d[i + 3] = 0;
        }
        ctx.putImageData(data, 0, 0);
        resolve(c.toDataURL('image/png'));
      } catch (err) { reject(err); }
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

export async function fileToBitmap(file) {
  try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); }
  catch { return await createImageBitmap(file); }
}

export async function urlToBitmap(dataUrl) {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return await createImageBitmap(blob);
}

// Cover-fit crop params for a source of size (ew,eh) into a box (w,h).
export function coverParams(ew, eh, w, h) {
  const ir = ew / eh, cr = w / h;
  let sw, sh, sx, sy;
  if (ir > cr) { sh = eh; sw = sh * cr; sx = (ew - sw) / 2; sy = 0; }
  else { sw = ew; sh = sw / cr; sx = 0; sy = (eh - sh) / 2; }
  return { sx, sy, sw, sh };
}

// Draw an ImageBitmap/Image with cover-fit, using progressive downscale for sharp big crops.
export function drawCover(ctx, img, x, y, w, h) {
  const { sx, sy, sw, sh } = coverParams(img.width, img.height, w, h);
  const factor = Math.min(sw / w, sh / h);
  if (factor > 2) {
    let cw = Math.round(sw), ch = Math.round(sh);
    let tmp = document.createElement('canvas');
    tmp.width = cw; tmp.height = ch;
    let tctx = tmp.getContext('2d');
    tctx.imageSmoothingEnabled = true; tctx.imageSmoothingQuality = 'high';
    tctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
    while (cw > w * 2 && ch > h * 2) {
      const nw = Math.max(Math.round(w), Math.round(cw / 2));
      const nh = Math.max(Math.round(h), Math.round(ch / 2));
      const next = document.createElement('canvas');
      next.width = nw; next.height = nh;
      const nctx = next.getContext('2d');
      nctx.imageSmoothingEnabled = true; nctx.imageSmoothingQuality = 'high';
      nctx.drawImage(tmp, 0, 0, cw, ch, 0, 0, nw, nh);
      tmp = next; tctx = nctx; cw = nw; ch = nh;
    }
    ctx.drawImage(tmp, 0, 0, cw, ch, x, y, w, h);
    return;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

// Draw a generic source element (e.g. <video>) with explicit dims, cover-fit (single step — for per-frame use).
export function drawCoverEl(ctx, el, ew, eh, x, y, w, h) {
  if (!ew || !eh) return;
  const { sx, sy, sw, sh } = coverParams(ew, eh, w, h);
  ctx.drawImage(el, sx, sy, sw, sh, x, y, w, h);
}

// A left-accented dark label pill (e.g. BEFORE / AFTER).
export function pill(ctx, text, x, y, scale, accent) {
  ctx.font = `900 ${Math.round(34 * scale)}px Arial, sans-serif`;
  const padX = 22 * scale, h = 52 * scale;
  const tw = ctx.measureText(text).width;
  ctx.fillStyle = 'rgba(15,23,42,0.92)';
  ctx.fillRect(x, y, tw + padX * 2, h);
  ctx.fillStyle = accent;
  ctx.fillRect(x, y, 8 * scale, h);
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.fillText(text, x + padX, y + h / 2 + 2 * scale);
}

// Corner watermark logo, optionally on a white backing card (skipped when transparent).
export function drawLogoWatermark(ctx, logoBmp, { areaW, areaH, pos = 'br', size = 'm', scale = 1, transparent = false }) {
  if (!logoBmp) return;
  const baseH = { s: 90, m: 130, l: 180 }[size] || 130;
  const lh = Math.round(baseH * scale);
  const lw = Math.round(lh * (logoBmp.width / logoBmp.height));
  const pad = Math.round(16 * scale), m = Math.round(36 * scale);
  const x = pos === 'tl' || pos === 'bl' ? m : areaW - lw - m - pad * 2;
  const y = pos === 'tl' || pos === 'tr' ? m : areaH - lh - m - pad * 2;
  if (!transparent) {
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillRect(x, y, lw + pad * 2, lh + pad * 2);
  }
  ctx.drawImage(logoBmp, x + pad, y + pad, lw, lh);
}
